import { useEffect, useRef, type ReactNode } from "react";

import { cn } from "@/lib/cn";
import { NARROW_QUERY, useMediaQuery } from "@/lib/useMediaQuery";

/**
 * 좁은 화면에서 옆에서 밀려 나오는 서랍.
 *
 * 디스코드 모바일과 같은 구조다. **대화가 늘 바탕에 있고** 목록이 그 위를 덮는다.
 * 목록 화면과 대화 화면을 오가는 방식(카카오톡)과는 다른데, 차이는 "대화가
 * 어디 있는가"다. 오가는 방식에서는 목록을 볼 때 대화가 화면에서 사라지지만,
 * 서랍은 대화를 그 자리에 둔 채 잠깐 가린다 — 그래서 닫으면 보던 자리로 그대로
 * 돌아온다.
 *
 * 넓은 화면에서는 이 컴포넌트가 아무 일도 하지 않는다. 서랍이 아니라 그냥
 * 왼쪽에 놓인 칸이 된다.
 */

type NavDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
};

/** 이만큼 끌면 열거나 닫는다. 조금 흔들린 것과 그러려던 것을 가른다. */
const DRAG_THRESHOLD_PX = 60;

/** 화면 왼쪽 이 안쪽에서 시작한 끌기만 서랍을 연다. */
const EDGE_ZONE_PX = 24;

type DragStart = { x: number; y: number };

export function NavDrawer({ open, onOpenChange, children }: NavDrawerProps) {
  const drag = useRef<DragStart | null>(null);
  /**
   * 지금 이게 **서랍인가**. 넓은 화면에서는 그냥 왼쪽 칸이라 여닫는다는 개념이 없다.
   *
   * 배치는 아래 `md:` 클래스들이 가르는데, `inert`만은 CSS로 못 가른다 —
   * 미디어 쿼리가 닿지 않는 HTML 속성이라 넓은 화면에서도 그대로 걸린다.
   * 그래서 여기만 자바스크립트로 묻는다.
   */
  const narrow = useMediaQuery(NARROW_QUERY);

  // Esc로 닫는다. 덮개를 누를 수 없는 키보드 사용자에게는 이게 유일한 출구다.
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onOpenChange]);

  /**
   * 화면 왼쪽 가장자리에서 쓸어서 연다.
   *
   * 닫혀 있을 때는 서랍을 잡을 것이 화면에 없다. 단추만 두면 대화를 읽다가
   * 목록으로 가려고 매번 위쪽 구석까지 손을 올려야 한다. 디스코드도 가장자리
   * 제스처로 연다.
   */
  useEffect(() => {
    if (open) return;

    const onPointerDown = (event: PointerEvent) => {
      // 마우스는 제외한다. 데스크톱에서 왼쪽 끝을 클릭하다 서랍이 튀어나오면
      // 무엇을 건드린 건지 알 수 없다.
      if (event.pointerType === "mouse") return;
      if (event.clientX > EDGE_ZONE_PX) return;
      drag.current = { x: event.clientX, y: event.clientY };
    };

    const onPointerMove = (event: PointerEvent) => {
      const start = drag.current;
      if (!start) return;

      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      // 세로로 더 움직였으면 스크롤이다. 서랍이 끼어들면 대화를 못 읽는다.
      if (Math.abs(dy) > Math.abs(dx)) {
        drag.current = null;
        return;
      }
      if (dx > DRAG_THRESHOLD_PX) {
        drag.current = null;
        onOpenChange(true);
      }
    };

    const end = () => {
      drag.current = null;
    };

    window.addEventListener("pointerdown", onPointerDown, { passive: true });
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("pointerup", end, { passive: true });
    window.addEventListener("pointercancel", end, { passive: true });

    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
    };
  }, [open, onOpenChange]);

  return (
    <>
      {/*
        덮개. 열린 동안 뒤쪽을 누르면 닫힌다. 서랍 자체가 없는 넓은 화면에는
        덮개도 없다. 사라질 때 같이 흐려져야 "덮여 있던 것이 걷혔다"로 읽힌다.
      */}
      <div
        aria-hidden
        onClick={() => onOpenChange(false)}
        className={cn(
          "ease-out-soft fixed inset-0 z-30 bg-[rgb(23_21_15_/_0.4)] transition-opacity duration-200 md:hidden",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />

      <div
        // 닫혀 있는 동안은 키보드와 스크린리더에서도 뺀다. 화면 밖에 있는
        // 목록에 초점이 걸리면 지금 어디에 있는지 알 수 없다.
        //
        // **좁을 때만이다.** 폭을 안 보고 걸었더니 넓은 화면에서 방을 하나라도
        // 열면(그때 navOpen이 false가 된다) 레일과 방 목록이 통째로 죽었다 —
        // 레일 아래 내 프로필이 안 눌리던 것이 이것이다.
        inert={narrow && !open}
        onPointerDown={(event) => {
          if (event.pointerType === "mouse") return;
          drag.current = { x: event.clientX, y: event.clientY };
        }}
        onPointerMove={(event) => {
          const start = drag.current;
          if (!start) return;

          const dx = event.clientX - start.x;
          const dy = event.clientY - start.y;
          if (Math.abs(dy) > Math.abs(dx)) {
            drag.current = null;
            return;
          }
          // 왼쪽으로 밀면 닫는다.
          if (dx < -DRAG_THRESHOLD_PX) {
            drag.current = null;
            onOpenChange(false);
          }
        }}
        onPointerUp={() => {
          drag.current = null;
        }}
        className={cn(
          // 좁은 화면: 화면 위에 떠서 밀려 나온다. 폭을 명시해야 안쪽 목록이
          // 기댈 곳이 생긴다 — 내용에 맞춰 늘어나면 `w-full`이 제자리를 돈다.
          // 화면을 다 덮지 않고 오른쪽을 남기는 것은, 덮인 대화가 뒤에 있다는
          // 것을 보여주기 위해서다(그래서 거기를 누르면 닫힌다).
          "ease-out-soft fixed inset-y-0 left-0 z-40 flex w-[86vw] transition-transform duration-250",
          // 넓은 화면: 서랍이 아니라 그냥 왼쪽 칸이다. 움직이지도 덮지도 않는다.
          "md:static md:z-auto md:w-auto md:translate-x-0 md:shadow-none md:transition-none",
          open ? "translate-x-0 shadow-modal" : "-translate-x-full",
        )}
      >
        {children}
      </div>
    </>
  );
}
